import { useContext, useEffect, useRef, useState } from 'react';
import type { SearchProps, SearchItemProps } from './types';
import { trackEvent, TrackingContext } from '@gjensidige/core-analytics';
import {
  Flex,
  Input,
  Label,
  Link,
  Text,
  Title
} from '@gjensidige/builders-components';
import styles from './search.module.less';

type States = 'idle' | 'loading' | SearchItemProps[];
let timeout: ReturnType<typeof setTimeout> | number;
let ajax: XMLHttpRequest; // Using old shcool XMLHttpRequest as this has native abort functionality

export const Search = ({
  action,
  autoFocus = true,
  inputLabel,
  messages,
  productsLabel,
  query,
  searchAPIUrl
}: SearchProps) => {
  const { options: trackingOptions } = useContext(TrackingContext);
  const [state, setState] = useState<States>('idle');
  const searchRef = useRef<HTMLInputElement>(null);
  const label = inputLabel || 'Search';

  const onChange = () => {
    const value = searchRef.current?.value || '';
    const shouldSearch = value.length >= 2;
    setState(shouldSearch ? 'loading' : 'idle');
    clearTimeout(timeout);

    if (ajax) ajax.abort(); // Cancel previous call
    if (searchAPIUrl && shouldSearch) {
      timeout = setTimeout(() => {
        if (!ajax) ajax = new XMLHttpRequest();
        ajax.onload = () => onResults(JSON.parse(ajax.responseText));
        ajax.open('POST', searchAPIUrl, true);
        ajax.setRequestHeader('Content-Type', 'application/json');
        ajax.send(JSON.stringify({ query: value }));
      }, 2000);
    }
  };

  const onResults = (data: SearchItemProps[]) => {
    setState(data);
    trackEvent(
      {
        eventAction: 'search',
        eventCategory: 'site-search',
        eventKey: 'input',
        eventLabel: searchRef.current?.value || '',
        eventStatus: data?.length ? 'success' : 'error'
      },
      trackingOptions
    );
  };

  useEffect(() => {
    setTimeout(() => {
      const input = searchRef.current;
      const event = new Event('input', { bubbles: true }); // Allow react to render before setting focus
      if (autoFocus) input?.focus();
      if (!ajax) input?.dispatchEvent(event); // Trigger search on first render
    }, 300);
  }, [autoFocus, state, query]);

  return (
    <>
      <form className={styles.search} method="get" action={action?.href}>
        <Label>
          <Title as="span" size="5" text={label} className={styles.title} />
          <Input
            autoComplete="off"
            autoFocus={autoFocus}
            classNameWrapper={styles.input}
            defaultValue={query}
            name="q"
            onChange={onChange}
            ref={searchRef}
            required
            tracking={false}
            type="search"
          />
        </Label>
      </form>
      {state === 'loading' && <div className={styles.loading} />}
      {!!(Array.isArray(state) && state.length) && (
        <>
          <Text aria-live="polite" as="h2">
            {messages.result?.title || 'Search results:'}{' '}
            {messages.result?.[state.length === 1 ? 'hit' : 'hits']?.replace(
              '!!',
              `${state.length}`
            )}
          </Text>
          <Flex
            className={styles.products}
            data-title={productsLabel || undefined}
            gap={{ xs: 'sm xs', md: 'sm' }}
          >
            {state.map(
              ({ product, link }, index) =>
                product && (
                  <Link key={index} href={link.url}>
                    {product}
                  </Link>
                )
            )}
          </Flex>
          <div className={styles.items}>
            {state.map(({ title, preface, link }: SearchItemProps, index) => (
              <a key={index} href={link.url} className={styles.item}>
                <Title as="h3" size="5" text={title} />
                {!preface || <Text size="small">{preface}</Text>}
                <Text size="small" className={styles.breadcrumb}>
                  {breadCrumbManipulator(link.url)}
                </Text>
              </a>
            ))}
          </div>
        </>
      )}
      {state.length === 0 && (
        <>
          <strong aria-live="polite">
            {messages.noResult?.title || 'No results'}
          </strong>
          <div>{messages.noResult?.description || ''}</div> {/* TODO EIRIK */}
        </>
      )}
      {state === 'idle' && (
        <>
          <strong aria-live="polite">
            {messages.information?.title || ''}
          </strong>
          <div>{messages.information?.description || ''}</div>
        </>
      )}
    </>
  );
};

const breadCrumbManipulator = (target: string) =>
  target
    .slice(1)
    .replace(/-/g, ' ')
    .split('/')
    .map((title) => title.slice(0, 1).toUpperCase() + title.slice(1))
    .join(' → ');
